export type MessageSeenEntry = {
  userId: string;
  seenAt?: Date | string | null;
};

export type MessagingItem = {
  id?: string | number | null;
  senderId?: string | number | null;
  seenAt?: Date | string | null;
  seenBy?: MessageSeenEntry[] | null;
  status?: string | null;
  isSeen?: boolean;
  raw?: Record<string, unknown> | null;
  [key: string]: unknown;
};

const normalizeId = (value: unknown): string | null => {
  if (value == null) {
    return null;
  }
  const candidate = String(value).trim();
  return candidate.length ? candidate : null;
};

const normalizeDate = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const updateSeenByEntry = (
  entries: MessagingItem['seenBy'],
  userId: string,
  seenAt: Date,
): MessageSeenEntry[] | null => {
  const base = Array.isArray(entries) ? entries : [];
  const existingIndex = base.findIndex((entry) => normalizeId(entry?.userId) === userId);

  if (existingIndex === -1) {
    return [...base, { userId, seenAt }];
  }

  const existing = base[existingIndex];
  const existingSeenAt = normalizeDate(existing?.seenAt);

  if (existingSeenAt && existingSeenAt >= seenAt) {
    return base;
  }

  return base.map((entry, index) =>
    index === existingIndex ? { ...entry, userId, seenAt } : entry,
  );
};

export const applyMarkSeenOptimistic = (
  messages: MessagingItem[] | undefined,
  messageIds: Array<string | number>,
  currentUserId: string | number | null | undefined,
  seenAt: Date,
): MessagingItem[] | undefined => {
  if (!Array.isArray(messages) || !messageIds.length || !currentUserId) {
    return messages;
  }

  const normalizedIds = new Set(messageIds.map((id) => String(id)));
  const viewerId = normalizeId(currentUserId);

  if (!viewerId) {
    return messages;
  }

  let mutated = false;

  const updated = messages.map((message) => {
    const messageId = normalizeId(message?.id);
    if (!message || !messageId || !normalizedIds.has(messageId)) {
      return message;
    }

    if (normalizeId(message.senderId) === viewerId) {
      return message;
    }

    const currentSeenAt = normalizeDate(message.seenAt);
    if (currentSeenAt && currentSeenAt <= seenAt) {
      return message;
    }

    const nextSeenBy = updateSeenByEntry(message.seenBy, viewerId, seenAt);
    if (!nextSeenBy) {
      return message;
    }

    mutated = true;
    return {
      ...message,
      seenAt,
      isSeen: true,
      seenBy: nextSeenBy,
      raw: {
        ...(message.raw ?? {}),
        seenAt: seenAt.toISOString(),
        seenBy: nextSeenBy,
      },
    };
  });

  return mutated ? updated : messages;
};

export const applyRealtimeMessageSeen = (
  messages: MessagingItem[] | undefined,
  messageIds: Array<string | number>,
  actorUserId: string | number | null | undefined,
  currentUserId: string | number | null | undefined,
  seenAt: Date,
): MessagingItem[] | undefined => {
  if (!Array.isArray(messages) || !messageIds.length || !actorUserId) {
    return messages;
  }

  const normalizedIds = new Set(messageIds.map((id) => String(id)));
  const actorId = normalizeId(actorUserId);
  const viewerId = normalizeId(currentUserId);

  if (!actorId) {
    return messages;
  }

  let mutated = false;

  const updated = messages.map((message) => {
    const messageId = normalizeId(message?.id);
    if (!message || !messageId || !normalizedIds.has(messageId)) {
      return message;
    }

    if (actorId === viewerId) {
      if (normalizeId(message.senderId) === actorId) {
        return message;
      }

      const currentSeenAt = normalizeDate(message.seenAt);
      if (currentSeenAt && currentSeenAt <= seenAt) {
        return message;
      }

      const nextSeenBy = updateSeenByEntry(message.seenBy, actorId, seenAt);
      if (!nextSeenBy || nextSeenBy === message.seenBy) {
        return message;
      }

      mutated = true;
      return {
        ...message,
        seenAt,
        isSeen: true,
        seenBy: nextSeenBy,
        raw: {
          ...(message.raw ?? {}),
          seenAt: seenAt.toISOString(),
          seenBy: nextSeenBy,
        },
      };
    }

    const baseSeenBy = Array.isArray(message.seenBy) ? message.seenBy : [];
    const nextSeenBy = updateSeenByEntry(baseSeenBy, actorId, seenAt) ?? baseSeenBy;
    const isOwnMessage = viewerId && normalizeId(message.senderId) === viewerId;
    const nextStatus = isOwnMessage ? 'read' : message.status ?? null;

    if (nextSeenBy === baseSeenBy && nextStatus === message.status) {
      return message;
    }

    mutated = true;
    return {
      ...message,
      seenBy: nextSeenBy,
      status: nextStatus ?? undefined,
      raw: {
        ...(message.raw ?? {}),
        seenBy: nextSeenBy,
        status: nextStatus ?? undefined,
      },
    };
  });

  return mutated ? updated : messages;
};
